import { z } from 'zod';

/**
 * Reusable content blocks (BB-57, CNT-002). Linked: a message writes `{% content_block 'key' %}` and renders the block's
 * body; published content keeps the version pinned at publish until an explicit propagate. Copy: the composer inserts
 * the body itself, with no link. A block never includes another block; a body is at most 50 KB.
 */
export const CONTENT_BLOCK_KINDS = ['text', 'html_subset'] as const;
export const ContentBlockKind = z.enum(CONTENT_BLOCK_KINDS);
export type ContentBlockKind = z.infer<typeof ContentBlockKind>;

export const CONTENT_BLOCK_BODY_MAX_BYTES = 51_200;

const Key = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/, 'lowercase letters, digits and _; starts with a letter');
const Name = z.string().trim().min(1).max(200);
const Description = z.string().trim().max(1000);
const Notes = z.string().trim().max(500);
/** UTF-16 length is checked loosely here; the service checks the 50 KB byte limit. */
const Body = z.string().min(1).max(CONTENT_BLOCK_BODY_MAX_BYTES);

export const ContentBlockListQuery = z.object({
  query: z.string().trim().max(255).optional(),
  archived: z.enum(['true', 'false']).default('false'),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ContentBlockListQuery = z.infer<typeof ContentBlockListQuery>;

export const CreateContentBlockBody = z.strictObject({ key: Key, name: Name, kind: ContentBlockKind.default('text'), description: Description.optional(), body: Body, notes: Notes.optional() });
export type CreateContentBlockBody = z.input<typeof CreateContentBlockBody>;

export const UpdateContentBlockBody = z
  .strictObject({ name: Name.optional(), description: Description.nullable().optional() })
  .refine((b) => b.name !== undefined || b.description !== undefined, 'nothing to update');
export type UpdateContentBlockBody = z.infer<typeof UpdateContentBlockBody>;

export const AddContentBlockVersionBody = z.strictObject({ body: Body, notes: Notes.optional(), baseVersion: z.int().min(1).optional() });
export type AddContentBlockVersionBody = z.infer<typeof AddContentBlockVersionBody>;

/** A published message: the running revision of a campaign or the published version of a Canvas. */
export const PropagateTarget = z.strictObject({ type: z.enum(['campaign', 'canvas']), id: z.uuid() });
export type PropagateTarget = z.infer<typeof PropagateTarget>;

export const PropagateContentBlockBody = z.strictObject({ toVersion: z.int().min(1), targets: z.array(PropagateTarget).min(1).max(200) });
export type PropagateContentBlockBody = z.infer<typeof PropagateContentBlockBody>;

export interface ContentBlockPersonRef {
  id: string;
  email: string | null;
}

export interface ContentBlockSummary {
  id: string;
  apiId: string;
  key: string;
  kind: ContentBlockKind;
  name: string;
  description: string | null;
  currentVersion: number;
  /** Campaigns and Canvases that link the block (working drafts and running content). */
  inclusionCount: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  updatedBy: ContentBlockPersonRef | null;
  archivedAt: string | null;
}

export interface ContentBlockVersionView {
  version: number;
  body: string;
  bytes: number;
  notes: string | null;
  createdBy: ContentBlockPersonRef | null;
  createdAt: string;
}

export interface ContentBlockUsageView {
  objectType: string;
  objectId: string;
  name: string;
  via: string;
  status?: 'draft' | 'scheduled' | 'active' | 'paused' | 'inactive';
  url?: string;
  /** The version the running content renders; null for an object that links the block only in its draft. */
  pinnedVersion: number | null;
}

export interface ContentBlockDetails extends ContentBlockSummary {
  /** Newest first, without the body. */
  versions: Array<Omit<ContentBlockVersionView, 'body'>>;
  current: ContentBlockVersionView;
  usage: ContentBlockUsageView[];
  /** The tag a message links the block with. */
  tag: string;
}

export interface ContentBlockIssue {
  code: string;
  message: string;
  line?: number;
  column?: number;
}

export interface ContentBlockArchiveResult extends ContentBlockDetails {
  /** Draft campaigns and Canvases that link it (active ones refuse the archive). */
  warnings: ContentBlockUsageView[];
}

export interface PropagateResult {
  toVersion: number;
  updated: Array<{ type: 'campaign' | 'canvas'; id: string; fromVersion: number | null }>;
}
